/**
 * Reines Modell des Mitarbeiter-Cockpits (DashboardView).
 *
 * Alles, was sich aus der Projektliste RECHNEN laesst — Agenda, Kennzahlen,
 * Zustaendigkeit, Vertretungsfortschritt, Fortschrittszeilen —, steht hier
 * ohne React, damit es ohne jsdom pruefbar ist. Die Komponenten unter
 * `components/dashboard/` zeichnen nur, was hier herauskommt.
 *
 * Bezugstag ist immer ein `todayKey` (YYYY-MM-DD, Ortszeit), NIE `Date.now()`
 * im Rechenweg: so rechnet ein Memo nur neu, wenn der Tag wechselt, und die
 * Tests koennen einen festen Tag hereinreichen.
 */
import { ReportMetadata } from '../../types';
import { getEffectiveDueDate, parseDateLocal, formatDateLocal, getCalendarWeek, heuteBerlin } from '../../services/dateService';
import { getProjectRelation } from '../../services/projectAccess';
import { istUebungsprojekt } from '../../services/uebung/uebungService';
import { getActiveSubstitutionFor, isUserActiveSubstitute } from '../../services/substitutionService';
import { istSifaProjekt } from '../../services/projectType';
import { contractedQuantityOfRow, preferNonNep } from '../../services/nepPosition';
import { getRepresentatives } from '../../services/projectRepresentatives';
import { getServiceLog } from '../../services/serviceJournal';
import { inspectionProofCount, isFlatRatePosition } from '../../services/projectLv';
import { substitutionFigures } from '../../services/substitutionProgress';

// ─── Formen ───────────────────────────────────────────────────────────────

/**
 * Die Projektzeile, wie sie aus der Liste kommt (`projects` in App.tsx).
 * Strukturell und offen: die Liste traegt mehr Spalten, als hier gebraucht
 * werden, und die Helfer daneben (`getEffectiveDueDate` & Co.) lesen sie
 * ohnehin als beliebiges Objekt.
 */
export interface DashboardProject {
    id: string;
    name?: string | null;
    project_number?: string | null;
    status?: string | null;
    user_id?: string | null;
    transfer_target_id?: string | null;
    updated_at?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    next_scheduled_date?: string | null;
    last_inspection_date?: string | null;
    coordinator_name?: string | null;
    ist_uebung?: boolean | null;
    project_services?: ServiceRow[] | null;
    data: { metadata?: ReportMetadata | null; [key: string]: unknown };
    [key: string]: unknown;
}

/** Eine Zeile aus `project_services`, soweit das Cockpit sie liest. */
export interface ServiceRow {
    id: string;
    title?: string | null;
    assignment?: string | null;
    is_optional?: boolean | null;
    quantity_contracted?: number | string | null;
    quantity_used?: number | string | null;
    /** Deckung einer Monatspauschale — nur, um deren Nachweiszeilen zu erkennen. */
    flat_rate?: unknown;
}

export interface DashboardUser {
    id?: string | null;
    email?: string | null;
}

// ─── Tage ─────────────────────────────────────────────────────────────────

/** YYYY-MM-DD in Ortszeit. */
export const dayKey = (d: Date): string => formatDateLocal(d);

/** `key` um `n` Tage verschoben (Ortszeit, ohne UTC-Drift). */
export const addDaysKey = (key: string, n: number): string => {
    const d = parseDateLocal(key);
    if (!d) return key;
    d.setDate(d.getDate() + n);
    return formatDateLocal(d);
};

/** Ganze Tage von `fromKey` bis `toKey` (positiv = spaeter). */
export const diffDays = (fromKey: string, toKey: string): number => {
    const a = parseDateLocal(fromKey);
    const b = parseDateLocal(toKey);
    if (!a || !b) return 0;
    return Math.round((b.getTime() - a.getTime()) / 864e5);
};

/** Sonntag der laufenden Woche (Mo–So). Am Sonntag der Tag selbst. */
export const weekEndKey = (todayKey: string): string => {
    const d = parseDateLocal(todayKey);
    if (!d) return todayKey;
    const dow = d.getDay() || 7; // Mo=1 … So=7
    return addDaysKey(todayKey, 7 - dow);
};

export const calendarWeekOf = (todayKey: string): number => {
    const d = parseDateLocal(todayKey);
    return d ? getCalendarWeek(d) : 0;
};

/** „Montag, 28.09." — Gruppentitel der Agenda. */
export const dayLabel = (key: string): string => {
    const d = parseDateLocal(key);
    return d ? d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' }) : key;
};

/** „So. 27.09." — Kurzform fuer Kacheln. */
export const shortDayLabel = (key: string): string => {
    const d = parseDateLocal(key);
    return d ? d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : key;
};

export const greetingFor = (hour: number): string =>
    hour < 12 ? 'Guten Morgen' : hour < 18 ? 'Guten Tag' : 'Guten Abend';

/** Fälligkeit ohne Zeitanteil — `getEffectiveDueDate` kann ISO mit `T` liefern. */
export const dueKeyOf = (p: DashboardProject): string | null => {
    const n = getEffectiveDueDate(p);
    if (!n) return null;
    return n.includes('T') ? n.split('T')[0] : n;
};

export const projectStatus = (p: DashboardProject): string =>
    p.status || p.data?.metadata?.globalData?.status || 'planning';

// ─── Zustaendigkeit ───────────────────────────────────────────────────────

export interface RelevantProjects {
    /** Verantwortung ODER aktive Urlaubsvertretung — alles, was das Cockpit zeigt. */
    all: DashboardProject[];
    /** Nur aktive Urlaubsvertretungen (Banner); feste Vertreter zaehlen nicht. */
    subs: DashboardProject[];
    /** Dauerhaft zustaendig (Eigentuemer, Koordinator, Vertreterliste). */
    mine: DashboardProject[];
    /**
     * Begehungs-Zustaendigkeit: `all` ohne die Projekte, in denen der Nutzer
     * NUR Verantwortliche Planungsphase ist. Speist die Agenda („Mein Plan")
     * und damit die Kacheln ueberfaellig/heute/Woche — die Planungsperson
     * macht dort keine Begehungen und darf sie nicht als faellig vorgesetzt
     * bekommen. In `all`/`mine` bleibt das Projekt: Liste und Zugriff gelten
     * fuer sie unveraendert.
     */
    inspections: DashboardProject[];
}

const EMPTY_RELEVANT: RelevantProjects = { all: [], subs: [], mine: [], inspections: [] };

/**
 * ÜBUNGSPROJEKTE FALLEN IN KEINE AUSWERTUNG (docs/lernzentrum-tutorial-plan.md,
 * 1.11 und 8.2). Gefiltert wird hier und nicht beim Laden, weil die Liste als
 * Prop ankommt und dieselbe Liste zugleich die Projektverwaltung speist, die
 * Übungsprojekte sehr wohl zeigt.
 *
 * Offene Projektübernahme zählt bewusst NICHT — erst nach dem Annehmen
 * wandert das Projekt in die eigene Liste.
 */
export const selectRelevantProjects = (
    alleProjekte: ReadonlyArray<DashboardProject>,
    user: DashboardUser | null | undefined,
): RelevantProjects => {
    const myEmail = (user?.email || '').trim().toLowerCase();
    if (!user?.id || myEmail.length < 3) return EMPTY_RELEVANT;
    const me = { id: user.id, email: myEmail };

    const uniqueMap = new Map<string, DashboardProject>();
    alleProjekte.forEach(p => {
        if (istUebungsprojekt(p)) return;
        const m = p.data?.metadata;
        if (!m) return;
        const rel = getProjectRelation(p, me);
        if (rel.isResponsible || rel.isSubstitute) uniqueMap.set(p.id, p);
    });
    const all = Array.from(uniqueMap.values());
    // Nur aktive Urlaubsvertretungen: feste Vertreter (Vertreterliste) sehen
    // das Projekt regulaer unter „Meine Projekte" und gehoeren nicht ins Banner.
    const subs = all.filter(p => isUserActiveSubstitute(p.data.metadata, myEmail));
    const mine = all.filter(p => getProjectRelation(p, me).isResponsible);
    // isPlanningOnly ist bei aktiver Urlaubsvertretung schon false — wer
    // vertritt, macht die Begehungen und behaelt sie in der Agenda.
    const inspections = all.filter(p => !getProjectRelation(p, me).isPlanningOnly);
    return { all, subs, mine, inspections };
};

export type RelationKind = 'own' | 'sub' | 'planning' | 'foreign';

/**
 * Beziehung des Nutzers zum Projekt — steuert nur noch das Etikett in
 * „In der Naehe". Gleiche Kriterien wie die RLS-Funktion can_access_project.
 * Ein fremdes Projekt oeffnet sich seit 25.09.2026 direkt (Nur-Ansicht,
 * Begruendung bei der ersten Begehung/Buchung — docs/mitarbeiter-cockpit-plan.md).
 *
 * 'planning': nur Verantwortliche Planungsphase. „Vertretung" waere dort
 * falsch — sie vertritt niemanden bei der Begehung, sie plant.
 */
export const relationKind = (p: DashboardProject, user: DashboardUser | null | undefined): RelationKind => {
    if (!user) return 'foreign';
    const rel = getProjectRelation(p, user);
    if (rel.isOwner || rel.isTransferTarget || rel.isCoordinator || rel.isPrimaryRepresentative) return 'own';
    if (rel.isPlanningOnly) return 'planning';
    // Urlaubsvertretung zählt nur im aktiven Zeitfenster (Start UND Ende geprüft)
    if (rel.hasAccess) return 'sub';
    return 'foreign';
};

export interface StatusCounts { active: number; planning: number; onHold: number; subs: number }

export const countStatus = (all: ReadonlyArray<DashboardProject>, subs: ReadonlyArray<DashboardProject>): StatusCounts => {
    let active = 0, planning = 0, onHold = 0;
    all.forEach(p => {
        const s = projectStatus(p);
        if (s === 'active') active++;
        else if (s === 'planning') planning++;
        else if (s === 'on_hold') onHold++;
    });
    return { active, planning, onHold, subs: subs.length };
};

// ─── Turnus ───────────────────────────────────────────────────────────────

/** Turnus in Worten — dieselbe Lesart wie dateService.calculateNextDueDate. */
export const rhythmLabel = (meta: ReportMetadata | null | undefined): string => {
    if (istSifaProjekt(meta)) return 'ohne Turnus';
    const c = meta?.intervalConfig;
    if (!c) return 'wöchentlich';
    switch (c.type) {
        case 'daily': return 'täglich';
        case 'weekly': return 'wöchentlich';
        case 'biweekly': return '14-tägig';
        case 'twice_monthly': return '2× im Monat';
        case 'monthly': return 'monatlich';
        case 'manual': return 'manuell';
        case 'custom': {
            const n = c.customDays || 7;
            // Bis 7 = Termine JE WOCHE, darueber = Tagesschritt (wie dateService).
            return n <= 7 ? `${n}× je Woche` : `alle ${n} Tage`;
        }
        default: return 'wöchentlich';
    }
};

// ─── Agenda („Mein Plan") ─────────────────────────────────────────────────

export type AgendaKind = 'overdue' | 'today' | 'tomorrow' | 'upcoming';

export interface AgendaItem {
    project: DashboardProject;
    id: string;
    number: string;
    name: string;
    address: string;
    /** Faelligkeit YYYY-MM-DD. */
    due: string;
    /** Tage ab heute: negativ = ueberfaellig, 0 = heute. */
    diff: number;
    kind: AgendaKind;
    sifa: boolean;
    /** Nicht mein eigenes Projekt (Vertretung/Vertreterliste). */
    isSub: boolean;
    rhythm: string;
    /** Ueberspringen setzt einen Turnus voraus (App.handleSkip bricht sonst ab). */
    canSkip: boolean;
}

export interface AgendaGroup {
    key: string;
    label: string;
    kind: AgendaKind;
    items: AgendaItem[];
}

export interface Agenda {
    items: AgendaItem[];
    groups: AgendaGroup[];
    overdue: number;
    today: number;
    /** Morgen bis Sonntag dieser Woche. */
    week: number;
    weekEnd: string;
    /** Sichtfenster: heute + `horizonDays`. */
    horizonEnd: string;
}

export const AGENDA_HORIZON_DAYS = 7;

/**
 * Faellige Begehungen: ueberfaellig, heute, und die naechsten Tage bis zum
 * Horizont. Nur aktive Projekte — SiFa-Betreuungen haben keinen Termin
 * (`getEffectiveDueDate` liefert null) und fehlen deshalb von selbst.
 *
 * Erwartet `RelevantProjects.inspections`, nicht `all`: Projekte, die der
 * Nutzer nur plant, gehoeren nicht in seinen Begehungsplan.
 */
export const buildAgenda = (
    all: ReadonlyArray<DashboardProject>,
    userId: string | null | undefined,
    todayKey: string,
    horizonDays: number = AGENDA_HORIZON_DAYS,
): Agenda => {
    const horizonEnd = addDaysKey(todayKey, horizonDays);
    const weekEnd = weekEndKey(todayKey);

    const items: AgendaItem[] = [];
    all.forEach(p => {
        if (projectStatus(p) !== 'active') return;
        const due = dueKeyOf(p);
        if (!due || due > horizonEnd) return;
        const m = (p.data?.metadata || {}) as ReportMetadata;
        const diff = diffDays(todayKey, due);
        const kind: AgendaKind = diff < 0 ? 'overdue' : diff === 0 ? 'today' : diff === 1 ? 'tomorrow' : 'upcoming';
        items.push({
            project: p,
            id: p.id,
            number: String(m.projectNumber || p.project_number || ''),
            name: p.name || m.projectName || '',
            address: m.projectAddress || '',
            due,
            diff,
            kind,
            sifa: istSifaProjekt(m),
            isSub: !!userId && p.user_id !== userId,
            rhythm: rhythmLabel(m),
            canSkip: !!m.intervalConfig,
        });
    });
    // Aeltestes zuerst; bei gleichem Tag nach Nummer/Name, damit die Liste
    // bei jedem Laden gleich steht.
    items.sort((a, b) => a.due.localeCompare(b.due) || a.number.localeCompare(b.number) || a.name.localeCompare(b.name));

    const groups: AgendaGroup[] = [];
    const byKey = new Map<string, AgendaGroup>();
    items.forEach(it => {
        const key = it.kind === 'upcoming' ? it.due : it.kind;
        let g = byKey.get(key);
        if (!g) {
            const label = it.kind === 'overdue' ? 'Überfällig' : it.kind === 'today' ? 'Heute' : it.kind === 'tomorrow' ? 'Morgen' : dayLabel(it.due);
            g = { key, label, kind: it.kind, items: [] };
            byKey.set(key, g);
            groups.push(g);
        }
        g.items.push(it);
    });

    return {
        items,
        groups,
        overdue: items.filter(i => i.diff < 0).length,
        today: items.filter(i => i.diff === 0).length,
        week: items.filter(i => i.diff >= 1 && i.due <= weekEnd).length,
        weekEnd,
        horizonEnd,
    };
};

// ─── Kennzahlen („Heute") ─────────────────────────────────────────────────

export interface Kpis {
    overdue: number;
    today: number;
    week: number;
    weekEnd: string;
    tasksOpen: number;
    tasksUrgent: number;
    unread: number;
    /** Versendete Berichte ohne Buchung; null = Sicht nicht erreichbar/noch nicht geladen. */
    unbooked: number | null;
}

/** Nichts Rotes, nichts Gelbes: die Kachelreihe darf „Alles erledigt" sagen. */
export const isAllClear = (k: Kpis): boolean =>
    k.overdue === 0 && k.today === 0 && k.tasksUrgent === 0 && k.unread === 0 && !(k.unbooked && k.unbooked > 0);

/** Eine Zeile Lagebericht fuer die Kachelreihe. */
export const summarizeStatus = (k: Kpis): string => {
    const teile: string[] = [];
    if (k.overdue > 0) teile.push(`${k.overdue} überfällig`);
    if (k.today > 0) teile.push(`${k.today} heute fällig`);
    if (k.tasksUrgent > 0) teile.push(`${k.tasksUrgent} dringende Aufgabe${k.tasksUrgent === 1 ? '' : 'n'}`);
    if (k.unread > 0) teile.push(`${k.unread} ungelesen`);
    if (k.unbooked && k.unbooked > 0) teile.push(`${k.unbooked} nicht gebucht`);
    if (teile.length === 0) {
        return k.week > 0
            ? `Alles erledigt — ${k.week} Begehung${k.week === 1 ? '' : 'en'} bis ${shortDayLabel(k.weekEnd)}`
            : 'Alles erledigt';
    }
    return teile.join(' · ');
};

// ─── Naehe, Suche, zuletzt benutzt ────────────────────────────────────────

export const haversineKm = (a: number, b: number, c: number, d: number): number => {
    const R = 6371, dLat = (c - a) * Math.PI / 180, dLon = (d - b) * Math.PI / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(a * Math.PI / 180) * Math.cos(c * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

export interface NearbyHit { p: DashboardProject; km: number }

/** Alle Projekte im Umkreis, naechstes zuerst — nicht nur das beste. */
export const nearbyProjects = (
    projects: ReadonlyArray<DashboardProject>,
    lat: number,
    lng: number,
    maxKm = 2,
): NearbyHit[] =>
    projects
        .map(p => {
            const c = p.data?.metadata?.globalData?.locationCoordinates as { lat?: number; lng?: number } | undefined;
            return (c?.lat && c?.lng) ? { p, km: haversineKm(lat, lng, c.lat, c.lng) } : null;
        })
        .filter((x): x is NearbyHit => !!x && x.km < maxKm)
        .sort((a, b) => a.km - b.km);

export const formatKm = (km: number): string =>
    km < 0.1 ? 'vor Ort' : `${km.toFixed(1).replace('.', ',')} km entfernt`;

export const recentProjects = (all: ReadonlyArray<DashboardProject>, n = 5): DashboardProject[] =>
    [...all]
        .sort((a, b) => Date.parse(b.updated_at || '') - Date.parse(a.updated_at || ''))
        .slice(0, n);

export const searchProjects = (all: ReadonlyArray<DashboardProject>, term: string): DashboardProject[] | null => {
    const t = term.trim().toLowerCase();
    if (!t) return null;
    return all.filter(p => (p.name || '').toLowerCase().includes(t) || String(p.project_number || '').includes(t));
};

// ─── Vertretungen ─────────────────────────────────────────────────────────

export interface SubstitutionProgress {
    isHoliday: boolean;
    subStart: Date | null;
    subEnd: Date | null;
    /** Kalendertage einschliesslich Start- und Endtag. */
    durationDays: number;
    target: number;
    completed: number;
    pct: number;
    daysLeft: number | null;
    coordinator: string;
    /** Wer ausser mir im Zeitraum begangen hat. */
    visitedByOthers: string[];
}

/**
 * Soll/Ist einer Urlaubsvertretung aus MEINER Sicht — mit derselben Rechnung
 * wie die Vertretungsuebersicht der Verwaltung (substitutionProgress.ts):
 * Soll aus den Turnusterminen, erledigt = jede Begehung im Zeitraum, auch
 * die eines Kollegen oder des Koordinators selbst (Betreiber, 23.10.2026).
 * Vorher zaehlten nur meine Buchungen, und das Soll war Dauer/Turnus ohne
 * Endtag — eine eintaegige Vertretung ergab 0/0.
 *
 * `parent`: die Mutter, wenn `p` eine Tochter ist — dort stehen ihre Buchungen.
 */
export const substitutionProgress = (
    p: DashboardProject,
    myEmail: string,
    userId: string | null | undefined,
    now: Date,
    parent?: DashboardProject | null,
): SubstitutionProgress => {
    const meta = (p.data?.metadata || {}) as ReportMetadata;
    // Eintrag des EIGENEN Users — bei mehreren gleichzeitig aktiven
    // Vertretern sonst falscher Name/Zeitraum im Banner
    const sc = getActiveSubstitutionFor(meta, myEmail, now);
    const isHoliday = !!sc;

    const subStart = sc ? parseDateLocal(sc.startDate) : null;
    const subEnd = sc ? parseDateLocal(sc.endDate) : null;
    let durationDays = 0;
    if (subStart && subEnd) durationDays = Math.round((subEnd.getTime() - subStart.getTime()) / 864e5) + 1;

    // Berliner Tag wie die Buchungstage der Zeilen (buchungstag).
    const figures = sc ? substitutionFigures({ project: p, entry: sc, parent, substituteId: userId, today: heuteBerlin(now) }) : null;
    const target = figures?.expected ?? 0;
    const completed = figures?.done ?? 0;
    const pct = Math.min(100, figures?.percentage ?? 0);
    const daysLeft = subEnd ? Math.max(0, Math.ceil((subEnd.getTime() - now.getTime()) / 864e5)) : null;
    const visitedByOthers = (figures?.otherVisitors || []).map(v => v.name).filter(Boolean);

    return { isHoliday, subStart, subEnd, durationDays, target, completed, pct, daysLeft, coordinator: meta.coordinator || '', visitedByOthers };
};

// ─── Meine Projekte (Fortschritt / Zeitstrahl) ────────────────────────────

export interface InspServiceRow { id: string; title: string; ct: number; us: number }

export interface ProgressRow {
    id: string;
    project: DashboardProject;
    name: string;
    number: string;
    status: string;
    ct: number;
    us: number;
    pct: number;
    start?: string | null;
    end?: string | null;
    selectedServiceId: string;
    selectedServiceTitle: string;
    allInspServices: InspServiceRow[];
    address: string;
    client: string;
    customer: string;
    reps: string[];
    due: string | null;
    sifa: boolean;
    otherServices: { title: string; ct: number; us: number }[];
}

const STATUS_ORDER: Record<string, number> = { active: 0, planning: 1, on_hold: 2 };

/**
 * Eine Zeile je laufendem/geplantem/ruhendem Projekt mit der Turnusposition
 * und ihrem Stand. Unveraendert aus dem alten Cockpit uebernommen.
 */
export const buildProgressRows = (
    all: ReadonlyArray<DashboardProject>,
    ganttServiceOverride: Readonly<Record<string, string>>,
): ProgressRow[] =>
    all
        .filter(p => {
            const s = projectStatus(p);
            return s === 'active' || s === 'planning' || s === 'on_hold';
        })
        .map(p => {
            const m = (p.data?.metadata || {}) as ReportMetadata;
            const status = projectStatus(p);
            const svcs = (p.project_services || []) as ServiceRow[];
            // Eine Pauschale ist keine Begehungsposition, auch mit Zuordnung
            // 'inspection': ihre Mengen sind Monate.
            const inspServices = svcs.filter(s => s.assignment === 'inspection' && !isFlatRatePosition(s));

            let ct = 0, us = 0, selectedServiceId = '', selectedServiceTitle = '';
            const allInspServices: InspServiceRow[] = [];

            if (inspServices.length > 0) {
                // Turnusposition = hoechste beauftragte Menge unter den
                // REGELpositionen (preferNonNep): eine NEP, auch eine viel
                // gebuchte, darf die Turnusrolle nicht uebernehmen. NEP
                // zaehlt ohnehin erst, was gebucht ist.
                const candidates = preferNonNep(inspServices);
                const turnus = candidates.reduce((best, cur) =>
                    contractedQuantityOfRow(cur) > contractedQuantityOfRow(best) ? cur : best
                , candidates[0]);
                // Im Pauschalprojekt waechst die Turnusposition seit der
                // Umstellung nicht mehr — die Begehungen stehen als
                // Nachweiszeilen auf der Pauschale. Sie gehoeren zum Turnus;
                // ohne sie stuende der Balken ab dem ersten Nachweis still.
                const proofs = inspectionProofCount(svcs, getServiceLog({ metadata: m }));
                const usedOf = (s: ServiceRow): number => (Number(s.quantity_used) || 0) + (s === turnus ? proofs : 0);
                const overrideId = ganttServiceOverride[p.id];
                const selectedService = overrideId
                    ? inspServices.find(s => s.id === overrideId) || inspServices[0]
                    : turnus;
                ct = contractedQuantityOfRow(selectedService);
                us = usedOf(selectedService);
                selectedServiceId = selectedService.id;
                selectedServiceTitle = selectedService.title || 'Begehung';
                inspServices.forEach(s => allInspServices.push({
                    id: s.id, title: s.title || 'Begehung',
                    ct: contractedQuantityOfRow(s), us: usedOf(s),
                }));
            }

            return {
                id: p.id,
                project: p,
                name: p.name || '',
                number: String(m.projectNumber || p.project_number || ''),
                status,
                ct, us,
                pct: ct > 0 ? Math.round(us / ct * 100) : 0,
                start: p.start_date || m.globalData?.startDate,
                end: p.end_date || m.globalData?.endDate,
                selectedServiceId,
                selectedServiceTitle,
                allInspServices,
                // Nur fuer die Hover-Karte: einmal hier eingesammelt statt bei
                // jedem Mausereignis erneut aus den Metadaten gefischt.
                address: m.projectAddress || '',
                client: m.clientName || '',
                customer: m.customerName || '',
                reps: getRepresentatives(m).map(r => r.name).filter(Boolean),
                due: getEffectiveDueDate(p),
                // SiFa-Betreuung: "Laufend" statt "Ausfuehrung" auf der Hover-Karte.
                sifa: istSifaProjekt(m),
                otherServices: svcs
                    .filter(s => s.assignment !== 'inspection' && !s.is_optional && (Number(s.quantity_contracted) || 0) > 0)
                    .map(s => ({
                        title: s.title || 'Leistung',
                        ct: Number(s.quantity_contracted) || 0,
                        us: Number(s.quantity_used) || 0,
                    })),
            };
        })
        // Sort: active first (by pct desc), then planning, then on_hold
        .sort((a, b) => {
            const oa = STATUS_ORDER[a.status] ?? 3, ob = STATUS_ORDER[b.status] ?? 3;
            if (oa !== ob) return oa - ob;
            return b.pct - a.pct;
        });
